/**
 * EventoRepo sobre Supabase. Misma lógica que DexieEventoRepo.
 * En create() deriva el código leyendo SO/AFE padre (lectura cruzada).
 */

import type { EventoRepo } from "../../repos/EventoRepo";
import type { Evento, So, Afe } from "@app-types/entities";
import type { TipoEvento } from "@constants/catalogos";
import { TIPOS_EVENTO } from "@constants/catalogos";
import { generarCodigoEvento } from "@features/eventos/eventosRules";
import { SupabaseBaseRepo } from "./SupabaseBaseRepo";

export class SupabaseEventoRepo extends SupabaseBaseRepo<Evento> implements EventoRepo {
  constructor(tenantId: string) {
    super("evento", tenantId);
  }

  async list(filter?: Partial<Evento>): Promise<Evento[]> {
    const all = await this.listAll();
    if (filter?.tipo) return all.filter((e) => e.tipo === filter.tipo);
    if (filter?.soId) return all.filter((e) => e.soId === filter.soId);
    return all;
  }

  async listBySo(soId: string): Promise<Evento[]> {
    return (await this.listAll()).filter((e) => e.soId === soId);
  }

  async listByRd(rdId: string): Promise<Evento[]> {
    return (await this.listAll()).filter((e) => e.rdId === rdId);
  }

  async listByTipo(tipo: TipoEvento): Promise<Evento[]> {
    return (await this.listAll()).filter((e) => e.tipo === tipo);
  }

  async listByRangoFechas(desde: string, hasta: string): Promise<Evento[]> {
    return (await this.listAll()).filter((e) => e.fecha >= desde && e.fecha <= hasta);
  }

  async countByTipo(): Promise<Record<TipoEvento, number>> {
    const all = await this.listAll();
    const counts = {} as Record<TipoEvento, number>;
    for (const t of TIPOS_EVENTO) counts[t] = 0;
    for (const e of all) {
      if (e.tipo in counts) counts[e.tipo as TipoEvento]++;
    }
    return counts;
  }

  async get(id: string): Promise<Evento | null> {
    return this.getById(id);
  }

  async create(data: Omit<Evento, "id" | "createdAt" | "updatedAt" | "version">): Promise<string> {
    const now = new Date().toISOString();
    let codigo = data.codigo;
    if (!codigo) {
      const [todos, sos, afes] = await Promise.all([
        this.listAll(),
        this.listOtherEntity<So>("so"),
        this.listOtherEntity<Afe>("afe"),
      ]);
      const soCodigo = data.soId ? sos.find((s) => s.id === data.soId)?.codigo : undefined;
      const afeCodigo = data.afeId ? afes.find((a) => a.id === data.afeId)?.codigo : undefined;
      codigo = generarCodigoEvento({
        ...(soCodigo ? { soCodigo } : {}),
        ...(afeCodigo ? { afeCodigo } : {}),
        codigosExistentes: todos.map((e) => e.codigo),
      });
    }
    const nuevo: Evento = {
      ...data,
      codigo,
      id: crypto.randomUUID(),
      tenantId: this.tenantId,
      createdAt: now,
      updatedAt: now,
      version: 1,
    };
    await this.createRaw(nuevo);
    return nuevo.id;
  }

  async update(id: string, data: Partial<Evento>, expectedVersion: number): Promise<void> {
    const current = await this.get(id);
    if (!current) throw new Error(`Evento ${id} not found`);
    const updated: Evento = {
      ...current,
      ...data,
      // Auditoria de creacion INMUTABLE: ver BaseRepo.update.
      createdBy: current.createdBy,
      createdAt: current.createdAt,
      version: current.version + 1,
      updatedAt: new Date().toISOString(),
    };
    await this.updateRaw(updated, expectedVersion);
  }

  async delete(id: string): Promise<void> {
    await this.deleteById(id);
  }
}
